export const hosting = {
  title: 'Web Hosting',
  subtitle: 'Manage your web hosting packages',
  loading: 'Loading packages...',
  buyPackage: 'Order hosting package',
  statuses: {
    provisioning: 'Provisioning',
    active: 'Active',
    failed: 'Failed',
    suspended: 'Stopped',
    deprovisioned: 'Deprovisioned'
  },
  phases: {
    Pending: 'Pending',
    Provisioning: 'Provisioning',
    Running: 'Running',
    Maintenance: 'Maintenance',
    Suspended: 'Suspended',
    Error: 'Error'
  },
  empty: {
    title: 'No hosting packages yet',
    description: 'Get started with a hosting package and bring your website online.',
    cta: 'Order package'
  },
  tabs: {
    overview: 'Overview',
    sftp: 'File Access',
    databases: 'Databases',
    settings: 'Settings',
    logs: 'Logs',
    console: 'Console',
    cron: 'Cron',
    backups: 'Backups',
    access: 'Access',
    actions: 'Actions'
  },
  delegation: {
    title: 'Delegated Access',
    subtitle: 'Grant other users access to this site',
    addDelegate: 'Grant Access',
    empty: 'No delegated users',
    emptyDescription: 'Grant other users access to manage this site.',
    sharedWithMe: 'Shared with me',
    delegatedBy: 'Shared by {email}',
    never: 'Never',
    add: {
      title: 'Grant Access',
      subtitle: 'Grant access to another user',
      email: 'Email Address',
      emailHint: 'The user must have an account on this system',
      presets: 'Presets',
      presetsHint: 'Quick selection for common permission combinations',
      permissions: 'Permissions',
      permissionsHint: 'Select the permissions the user should receive',
      expiry: 'Expiry Date',
      expiryHint: 'Leave empty for unlimited access',
      noExpiry: 'No expiry date'
    },
    editModal: {
      title: 'Edit Access',
      subtitle: 'Adjust permissions for {email}'
    },
    deleteModal: {
      title: 'Remove Access',
      message: 'Are you sure you want to remove {email}\'s access to this site?',
      warning: 'The user will immediately lose access to all delegated features.',
      confirm: 'Remove Access'
    },
    permissionLabels: {
      'hosting:sites:read': 'Read Site',
      'hosting:sites:write': 'Manage Site',
      'hosting:sites:delete': 'Delete Site',
      'hosting:backup:read': 'Read Backups',
      'hosting:backup:write': 'Manage Backups',
      'hosting:logs:read': 'Read Logs'
    },
    permissionGroups: {
      site: 'Site',
      backups: 'Backups',
      logs: 'Logs'
    },
    presetLabels: {
      hosting_readonly: 'Read-only',
      hosting_manage: 'Manage',
      hosting_full: 'Full Access'
    },
    notifications: {
      added: 'Access Granted',
      addedMessage: '{email} now has access to this site.',
      updated: 'Access Updated',
      updatedMessage: 'Permissions have been updated.',
      deleted: 'Access Removed',
      deletedMessage: '{email} no longer has access to this site.',
      accountNotFound: 'No account found with this email address.',
      alreadyDelegate: 'This user already has access to this site.',
      unexpectedResponse: 'Unexpected response from the server. Please reload the page.'
    }
  },
  overview: {
    status: 'Status',
    runtime: 'Runtime',
    domains: 'Domains',
    webServer: 'Web Server',
    noDomains: 'No domains',
    storage: 'Storage Usage',
    restart: 'Restart',
    restartSuccess: 'Site is restarting...',
    webspace: 'Webspace',
    database: 'Databases',
    mail: 'Email',
    otherSites: 'Other sites',
    thisSite: 'this site:'
  },
  settings: {
    runtimeTitle: 'Runtime & Web Server',
    runtimeHint: 'Changes the PHP version or web server your site runs on. While the change is applied, your site is unavailable for a few seconds.',
    runtimeFieldHint: 'Recommended: the newest PHP version your application is compatible with. Newer versions are faster and get security updates for longer. WordPress: current releases support every PHP from 8.0 up — older plugins / themes may lag behind. "Static" disables PHP entirely — pure HTML/JS/CSS only.',
    webServerFieldHint: 'Recommended: nginx — faster and the default for new sites. Pick Apache only if your application explicitly relies on .htaccess rewrite rules.',
    docrootLabel: 'Document root subdirectory',
    docrootHint: 'Leave empty for most sites (WordPress, Drupal, plain PHP, static HTML). Set to "public" for Laravel, Flarum or Symfony installs that keep their entry point in a public/ subfolder.',
    docrootInvalid: 'Subdirectory only — letters, digits, _ and -, optionally separated by /. No leading slash, no path traversal.',
    phpHint: 'Limits for PHP applications. Higher values allow larger uploads and longer scripts but cost more memory.',
    save: 'Save',
    saveConfirm: 'Saving will briefly take your site offline (a few seconds while the new runtime starts). Continue?',
    saved: 'Settings saved',
    savedHint: 'Your site is restarting briefly so the changes take effect.'
  },
  actions: {
    restart: {
      title: 'Restart site',
      hint: 'Restarts the software running your site. Useful when the site is unresponsive or stuck. The site is unavailable for ~5–15 seconds during the restart.',
      confirm: 'Your site will be unreachable for ~5–15 seconds. Restart now?'
    },
    maintenance: {
      title: 'Maintenance mode',
      hint: 'Shows visitors a maintenance page instead of your site. Enable for updates or database work.',
      messagePlaceholder: 'e.g. "Back at 2pm"',
      messageHint: 'Optional. Displayed alongside the default maintenance message.',
      confirmEnable: 'Visitors will see a maintenance page instead of your site. Enable now?',
      confirmDisable: 'End maintenance mode? Your site will be reachable normally again.'
    },
    plan: {
      title: 'Change plan',
      hint: 'Switch to a different hosting package. More storage, more sites per package.'
    },
    delete: {
      title: 'Delete site',
      hint: 'Removes the site permanently including files, databases and backups. This action cannot be undone.'
    }
  },
  sftp: {
    title: 'SFTP Access',
    host: 'Host',
    port: 'Port',
    username: 'Username',
    password: 'New password',
    rotate: 'Reset password',
    rotateConfirm: 'This will terminate any active SFTP sessions. Continue?',
    rotateTitle: 'Password reset',
    rotateSuccess: 'The new password is shown once. Save it now.',
    passwordOnceHint: 'This password will not be shown again. Save it in your password manager.',
    hint: 'Click a value to copy it. Connect using FileZilla, Cyberduck or WinSCP.',
    copied: 'Copied'
  },
  databases: {
    title: 'Databases',
    name: 'Name',
    type: 'Type',
    host: 'Host',
    database: 'Database',
    username: 'Username',
    password: 'Password',
    status: 'Status',
    actions: 'Actions',
    add: 'Add database',
    remove: 'Remove',
    credentials: 'Credentials',
    showCredentials: 'Show credentials',
    hideCredentials: 'Hide credentials',
    rotatePassword: 'Rotate password',
    rotateConfirm: 'Are you sure? The current password will be invalidated and your application must be updated.',
    rotateSuccess: 'Password is being rotated...',
    snippet: 'Config snippet',
    confirmRemove: 'Are you sure? The database and all data will be permanently deleted.',
    removeSuccess: 'Database is being removed...',
    addSuccess: 'Database is being created...',
    empty: 'No databases configured yet.',
    ready: 'Ready',
    notReady: 'Creating...',
    snippetFormats: {
      env: '.env',
      wordpress: 'WordPress',
      laravel: 'Laravel',
      php: 'PHP'
    },
    addDialog: {
      title: 'Add database',
      nameLabel: 'Name',
      nameHint: 'Starts with a lowercase letter, then lowercase letters and numbers only (max. 16 characters)',
      namePlaceholder: 'e.g. wordpress',
      typeLabel: 'Type',
      typeHint: 'MariaDB is currently the available engine (MySQL-compatible)',
      create: 'Create',
      cancel: 'Cancel'
    }
  },
  php: {
    title: 'PHP Settings',
    save: 'Save',
    saveSuccess: 'PHP settings saved',
    presets: 'Presets',
    preset: {
      default: 'Default',
      wordpress: 'WordPress',
      laravel: 'Laravel'
    },
    settings: {
      upload_max_filesize: 'Max upload size',
      post_max_size: 'Max POST size',
      max_execution_time: 'Max execution time',
      memory_limit: 'Memory limit',
      display_errors: 'Display errors',
      error_reporting: 'Error reporting'
    },
    warnings: {
      title: 'Heads up — this combination is rarely what you want:',
      postSmallerThanUpload: 'Max POST size is smaller than max upload size. File uploads at that size will silently fail. POST size should be at least as large as upload size.',
      memorySmallerThanPost: 'Memory limit is smaller than max POST size. Large uploads will run PHP out of memory and abort. Memory limit should be at least as large as POST size.',
      displayErrorsOn: '"Display errors" is On — PHP will print error messages with file paths and possibly source code directly to the browser. Security risk: development only, never production.'
    }
  },
  suspendedNotice: {
    title: 'Package ended, site offline',
    body: 'This site\'s package has ended, so the site is offline. Until the end of {date} you can reach your files over SFTP and create and download backups, which hold a SQL dump of each database. Each backup is kept for 72 hours. After that, the site is deleted. A final backup of its files and databases is kept for 14 more days and then deleted as well.',
    bodyUndated: 'This site\'s package has ended, so the site is offline. For 30 days after the package ended you can reach your files over SFTP and create and download backups, which hold a SQL dump of each database. Each backup is kept for 72 hours. After that, the site is deleted. A final backup of its files and databases is kept for 14 more days and then deleted as well.'
  },
  errors: {
    siteSuspended: 'This site\'s package has ended. You can still retrieve its data, but you can no longer change it.',
    packageEnded: 'This package has ended, so no more sites can be added to it.'
  },
  maintenance: {
    title: 'Maintenance Mode',
    enable: 'Enable maintenance mode',
    disable: 'Disable maintenance mode',
    message: 'Maintenance message (optional)',
    active: 'Maintenance mode active',
    success: 'Maintenance mode updated'
  },
  logs: {
    title: 'Logs',
    errorLogs: 'Error Logs',
    accessLogs: 'Access Logs',
    lines: 'Lines',
    timeRange: 'Time range',
    autoRefresh: 'Auto-refresh',
    noLogs: 'No logs found for the selected time range.',
    loading: 'Loading logs...',
    refresh: 'Refresh'
  },
  console: {
    title: 'Console',
    description: 'Run one-off commands directly against your site — for example composer install, php artisan migrate, or wp core update.',
    workingDirLabel: 'Working directory',
    workingDirPlaceholder: '/app/public_html (default)',
    workingDirHint: 'Command runs in {dir}',
    workingDirInvalid: 'Path must be /app/public_html or a subdirectory of it',
    commandLabel: 'Command',
    commandPlaceholder: 'php --version',
    runButton: 'Run',
    stopButton: 'Stop',
    templates: 'Templates',
    templatesEmpty: 'Pick a template…',
    output: 'Output',
    noOutput: 'No output yet — command is starting…',
    waiting: 'Starting up…',
    succeeded: 'Finished with exit code {code} (success)',
    failed: 'Failed (exit code {code})',
    errorEvent: 'Error: {message}',
    disconnected: 'Connection to server lost',
    recentRuns: 'Recent runs',
    recentEmpty: 'No runs yet.',
    viewLogs: 'View logs',
    status: {
      running: 'running',
      ok: 'OK',
      failed: 'Failed'
    },
    conflictInFlight: 'A command is already running on this site. Please wait or stop the current command.',
    logsExpired: 'Logs are no longer available — the job has expired.'
  },
  cron: {
    title: 'Scheduled tasks',
    description: 'Cron jobs that run on a schedule against your site — for example php artisan schedule:run, php flarum schedule:run, or Drupal cron.',
    empty: 'No cron jobs yet.',
    addButton: 'New cron job',
    tableName: 'Name',
    tableSchedule: 'Schedule',
    tableCommand: 'Command',
    tableTimeout: 'Timeout',
    tableConcurrency: 'Concurrency',
    tableActions: 'Actions',
    runs: 'Runs',
    edit: 'Edit',
    delete: 'Delete',
    runsTitle: 'Runs of {name}',
    runsEmpty: 'No runs found. Run history is retained for about 24 hours.',
    viewOutput: 'Output',
    runOutputEmpty: 'No output found for this run.',
    modalAddTitle: 'Add cron job',
    modalEditTitle: 'Edit cron job',
    fieldName: 'Name',
    fieldNameHint: 'Short slug (lowercase letters, digits, hyphens) — cannot be renamed once created.',
    fieldNameInvalid: 'Name must start with a lowercase letter and contain only lowercase letters, digits and hyphens (max 31 characters).',
    fieldSchedule: 'Schedule',
    fieldScheduleHint: 'Standard 5-field cron expression (minute hour dom month dow).',
    fieldScheduleInvalid: 'Invalid schedule',
    fieldCommand: 'Command',
    fieldCommandHint: 'Run with sh -c. Max 4096 characters.',
    fieldWorkingDir: 'Working directory',
    fieldWorkingDirHint: 'Optional. Path must be /app/public_html or a subdirectory of it. Empty = default.',
    fieldWorkingDirInvalid: 'Path must be /app/public_html or a subdirectory of it.',
    fieldTimeout: 'Timeout (seconds)',
    fieldTimeoutHint: '10–3600 seconds, empty = 300',
    fieldConcurrency: 'Concurrency',
    concurrencyDefault: '(default — Forbid)',
    concurrencyForbid: 'Forbid — skip new run if previous still running',
    concurrencyReplace: 'Replace — terminate previous run',
    concurrencyAllow: 'Allow — runs may overlap',
    save: 'Save',
    cancel: 'Cancel',
    errorQuotaSite: 'Maximum 5 cron jobs per site.',
    errorQuotaAccount: 'Maximum 30 cron jobs per account reached.',
    errorDuplicateName: 'A cron job with that name already exists.',
    errorInvalidSchedule: 'The schedule could not be parsed server-side.',
    errorGeneric: 'Cron job could not be saved.',
    successCreated: 'Cron job created.',
    successUpdated: 'Cron job updated.',
    successDeleted: 'Cron job deleted.'
  },
  backups: {
    title: 'Backups',
    create: 'Create backup',
    creating: 'Creating backup...',
    download: 'Download',
    downloadFiles: 'Download files',
    downloadDb: 'Download DB',
    filesSize: 'Files',
    empty: 'No backups yet.',
    rateLimited: 'Max 1 backup per hour, 3 per day.',
    busy: 'A backup or restore of this site is still running. Try again when it has finished.',
    expiresAt: 'Expires',
    size: 'Size',
    dbSize: 'Database',
    status: {
      in_progress: 'In progress...',
      completed: 'Completed',
      failed: 'Failed'
    },
    restore: {
      button: 'Restore',
      title: 'Restore from Backup',
      warning: 'This overwrites the databases in this backup. A backup of their current state is taken first and listed here, so you can go back.',
      restoreDb: 'Restore database',
      confirm: 'Restore',
      cancel: 'Cancel',
      started: 'Restore started...',
      notRestorable: 'Only a completed backup with a database can be restored.',
      limitReached: 'This site was restored {limit} times in the last 24 hours. Try again once the oldest of those restores is 24 hours old.',
      history: 'Restores',
      fromBackup: 'from the backup of {date}',
      fromBackupExpired: 'from a backup that has since expired',
      safetyBackup: 'State before the restore: backup of {date}',
      safetyBackupExpired: 'The backup of the state before the restore has expired',
      status: {
        in_progress: 'Restoring...',
        completed: 'Restored',
        failed: 'Failed'
      }
    }
  },
  delete: {
    button: 'Delete site',
    confirm: 'Are you sure? This will permanently delete the site and all associated data. Type the site name to confirm.',
    success: 'Site is being deleted...'
  },
  upgrade: {
    button: 'Switch',
    title: 'Change Hosting Plan',
    current: 'Current plan',
    noOptions: 'No upgrade options available.',
    success: 'Plan changed successfully'
  },
  package: {
    title: 'Package',
    quota: '{used} of {total} used',
    overage: '{amount} over package quota — overage will be billed',
    addSite: 'Add site',
    sitesCount: 'no sites | {count} site | {count} sites',
    noSites: 'No sites in this package yet'
  },
  addSite: {
    title: 'New site{package}',
    successTitle: 'Site created',
    successMessage: '{name} has been added to your package',
    submit: 'Create site',
    fields: {
      name: 'Name',
      nameHint: 'Short slug (lowercase letters, digits, hyphens)',
      domains: 'Domains',
      domainsPlaceholder: 'example.com, www.example.com',
      domainsHint: 'Comma- or space-separated. Registered domains or subdomains of your domains (e.g. freebie.your-domain.com).',
      runtime: 'Runtime',
      webServer: 'Web server',
      redirects: 'Redirects',
      redirectsHint: 'Optional: redirect extra hostnames to a primary domain with 301/302/308.'
    },
    redirect: {
      add: 'Redirect',
      remove: 'Remove',
      fromPlaceholder: 'www.yourdomain.com'
    },
    errors: {
      nameRequired: 'Name is required',
      domainRequired: 'At least one domain is required',
      redirectIncomplete: 'Each redirect needs a source and target.',
      redirectTargetNotPrimary: 'Target must be one of the primary domains.'
    }
  },
  siteDetail: {
    dnsTitle: 'DNS records',
    dnsHint: 'Every hostname of this site, including redirect sources, needs both records at the DNS provider of its domain. They are not set automatically.',
    dnsCopy: 'Copy',
    redirectsTitle: 'Redirects',
    redirectsHint: 'Redirect additional hostnames to a primary domain with 301/302/308.',
    addRedirect: 'Add redirect',
    removeRedirect: 'Remove',
    saveRedirects: 'Save redirects',
    redirectsSaved: 'Redirects updated',
    redirectsSaveError: 'Failed to save redirects',
    additionalHostnamesTitle: 'Additional hostnames',
    additionalHostnamesHint: 'Subdomains this site serves (e.g. www.example.com). The parent domain (example.com) must belong to your account.',
    addAdditionalHostname: 'Add hostname',
    removeAdditionalHostname: 'Remove',
    saveAdditionalHostnames: 'Save hostnames',
    additionalHostnamesSaved: 'Hostnames updated'
  },
  mailSender: {
    title: 'Mail from PHP',
    active: {
      intro: 'PHP mail() sends this site\'s mail through our mail server, including from cron jobs and the console. It sends as this address:',
      copy: 'Copy',
      hourlyLimit: 'At most {count} recipients per hour.',
      dailyLimit: 'At most {count} recipients per day.',
      fromRule: 'The From address must be on the site\'s domain: {domain} or a subdomain of it.',
      replyToRule: 'Visitor addresses, such as those from a contact form, belong in Reply-To, not in From.',
      refused: 'Mail that is refused is not queued for later: mail() returns false.'
    },
    pending: 'Mail sending for this site is being set up.',
    unavailable: {
      generic: 'This site cannot send mail from PHP right now.',
      no_custom_domain: {
        text: 'Add your own domain to the site to send mail from PHP.'
      },
      mail_elsewhere: {
        text: 'This domain\'s mail is hosted elsewhere. Use an SMTP plugin with your mail provider\'s credentials.',
        textDomain: 'Mail for {domain} is hosted elsewhere. Use an SMTP plugin with your mail provider\'s credentials.',
        link: 'Read the guide'
      },
      mail_off: {
        text: 'Mail is switched off or not included for this domain.',
        textDomain: 'Mail is switched off or not included for {domain}.',
        link: 'Open mail settings'
      },
      domain_unverified: {
        text: 'The domain is not verified for mail yet. Verify it first.',
        textDomain: '{domain} is not verified for mail yet. Verify the domain first.',
        link: 'Verify the domain'
      },
      dns_incomplete: {
        text: 'The SPF or DKIM record for this domain is missing.',
        textDomain: 'The SPF or DKIM record for {domain} is missing.',
        link: 'Set up mail DNS'
      },
      site_suspended: {
        text: 'The site is suspended and cannot send mail.'
      },
      static_runtime: {
        text: 'Static sites run no PHP, so they have no mail sender.'
      }
    }
  }
} as const
